function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { Deferred } from '@sail/utils';
import { MessageType } from '../../internal/rpc-channel/types';
import { v4 as uuid } from 'uuid';
const RPC_CHANNEL_SRC = 'sdk-rpc-channel';
const transportMessageTypes = new Set(Object.keys(MessageType));
class RpcChannel {
    isRelevantMessageEvent({ data }) {
        return !!data.message && transportMessageTypes.has(data.message.type) && this.name === data.from && data.source === RPC_CHANNEL_SRC;
    }
    // Channel Interface
    /**
   * Calls a given `method` with a given set of `args` remotely on the other side
   * `RpcChannel` that is assigned a role of "server".
   *
   * Returns a promise that is either resolved with a result of remote call or with
   * an error that could be thrown when invoking a given a handler or when no handler
   * was defined at all for a specified method on the other side of `RpcChannel`.
   */ call(method, ...args) {
        if (this.closed) {
            return Promise.reject(new Error(`Cannot call method '${method}'. RPC channel '${this.name}' is closed.`));
        }
        const id = uuid();
        const response = new Deferred();
        this.responses.set(id, response);
        this.sendMessage({
            type: MessageType.CALL,
            id,
            method: method,
            args
        });
        return response.promise.finally(()=>this.responses.delete(id));
    }
    /**
   * Creates a client subscription to a stream of messages generated
   * by the server. Each incoming message whose shape is defined by
   * `Payload` is passed to a `callback` function.
   */ watch(name, payload, callback) {
        if (this.closed) {
            throw new Error(`Cannot subscribe to '${name}'. RPC channel '${this.name}' is closed.`);
        }
        const id = uuid();
        this.callbacks.set(id, callback);
        this.sendMessage({
            id,
            type: MessageType.ADD_SUBSCRIPTION,
            name: name,
            payload
        });
        return ()=>{
            this.callbacks.delete(id);
            this.sendMessage({
                id,
                type: MessageType.REMOVE_SUBSCRIPTION
            });
        };
    }
    close() {
        var _this_transport_terminate, _this_transport;
        this.closed = true;
        (_this_transport_terminate = (_this_transport = this.transport).terminate) === null || _this_transport_terminate === void 0 ? void 0 : _this_transport_terminate.call(_this_transport);
        this.transport.removeEventListener('message', this.messageListener);
    }
    // Private Implementation
    sendMessage(message) {
        const encodedMessage = this.encoder ? this.encoder.encode(message) : message;
        this.log(`> SND ${message.type}, ${message.id}`);
        this.transport.postMessage({
            message: encodedMessage,
            from: this.name,
            source: RPC_CHANNEL_SRC
        });
    }
    handleMessage(message) {
        const decodedMessage = this.encoder ? this.encoder.decode(message) : message;
        switch(decodedMessage.type){
            case MessageType.CALL:
                this.handleCall(decodedMessage);
                break;
            case MessageType.CALL_RESPONSE:
                this.handleСallResponse(decodedMessage);
                break;
            case MessageType.CALL_ERROR:
                this.handleCallError(decodedMessage);
                break;
            case MessageType.ADD_SUBSCRIPTION:
                this.handleAddSubscription(decodedMessage);
                break;
            case MessageType.REMOVE_SUBSCRIPTION:
                this.handleRemoveSubscription(decodedMessage);
                break;
            case MessageType.SUBSCRIPTION_RESULT:
                this.handleSubscriptionResult(decodedMessage);
                break;
        }
    }
    handleCall({ method, id, args }) {
        var _this = this;
        return _async_to_generator(function*() {
            const handler = _this.handlers.get(method);
            if (handler) {
                try {
                    _this.log(`< RCV ${MessageType.CALL}, ${id}, ${method}, ${args}`);
                    _this.sendMessage({
                        id,
                        type: MessageType.CALL_RESPONSE,
                        result: yield handler(...args)
                    });
                } catch (err) {
                    _this.sendMessage({
                        id,
                        type: MessageType.CALL_ERROR,
                        error: err
                    });
                }
            } else {
                _this.sendMessage({
                    id,
                    type: MessageType.CALL_ERROR,
                    error: new Error(`Unsupported method: ${method}`)
                });
            }
        })();
    }
    handleСallResponse({ id, result }) {
        const resp = this.responses.get(id);
        if (resp) {
            this.log(`< RCV ${MessageType.CALL_RESPONSE}, ${id}, ${result}`);
            resp.resolve(result);
        }
    }
    handleCallError({ id, error }) {
        const resp = this.responses.get(id);
        if (resp) {
            this.log(`< RCV ${MessageType.CALL}, ${id}, ${error}`);
            resp.reject(error);
        }
    }
    handleSubscriptionResult({ id, result }) {
        const callback = this.callbacks.get(id);
        if (callback) {
            this.log(`< RCV ${MessageType.SUBSCRIPTION_RESULT}, ${id}, ${result}`);
            callback(result);
        }
    }
    handleAddSubscription({ id, name, payload }) {
        const producer = this.producers.get(name);
        if (producer && !this.subscriptions.has(id)) {
            this.log(`< RCV ${MessageType.ADD_SUBSCRIPTION}, ${id}, ${name}, ${payload}`);
            this.subscriptions.set(id, producer(payload, (result)=>{
                this.sendMessage({
                    id,
                    result,
                    type: MessageType.SUBSCRIPTION_RESULT
                });
            }));
        }
    }
    handleRemoveSubscription({ id }) {
        const subscription = this.subscriptions.get(id);
        if (subscription) {
            this.log(`< RCV ${MessageType.REMOVE_SUBSCRIPTION}, ${id}`);
            subscription.unsubscribe();
            this.subscriptions.delete(id);
        }
    }
    log(...args) {
        if (this.enableLogging) {
            // eslint-disable-next-line no-console
            console.log(...args);
        }
    }
    constructor({ name, transport, methods, subscriptions, enableLogging, encoder }){
        _define_property(this, "name", void 0);
        _define_property(this, "transport", void 0);
        _define_property(this, "enableLogging", void 0);
        _define_property(this, "responses", new Map());
        _define_property(this, "callbacks", new Map());
        _define_property(this, "handlers", new Map());
        _define_property(this, "subscriptions", new Map());
        _define_property(this, "messageListener", void 0);
        _define_property(this, "encoder", void 0);
        _define_property(this, "producers", new Map());
        _define_property(this, "closed", false);
        this.name = name;
        this.transport = transport;
        this.enableLogging = enableLogging !== null && enableLogging !== void 0 ? enableLogging : false;
        this.encoder = encoder;
        for(const name in methods){
            if (methods.hasOwnProperty(name)) {
                this.handlers.set(name, methods[name]);
            }
        }
        for(const name in subscriptions){
            if (subscriptions.hasOwnProperty(name)) {
                this.producers.set(name, subscriptions[name]);
            }
        }
        this.messageListener = (evt)=>{
            if (this.isRelevantMessageEvent(evt)) {
                this.handleMessage(evt.data.message);
            }
        };
        this.transport.addEventListener('message', this.messageListener);
    }
}
/**
 * An instance of `RpcChannel` represents one end of an asynchronous communications channel
 * that can be used for communicating between different browsing contexts or threads.
 * `RpcChannel` is independent of the underlying communication medium/transport so that
 * it's possible to use iframe, MessagePort or WebWorker or any other "messenger" that
 * implements an abstract `Transport` interface. For instance, in Node one can use a transport
 * built on top of MessagePort, while in browsers it's possible to use a Worker-based transport.
 *
 * When using `RpcChannel`, one instance of `RpcChannel` should be created on each side.
 * For instance, one side creates an `RpcChannel` that is assigned a role of client/caller,
 * while another one creates an `RpcChannel` that serves as an executor/server. The "server"
 * instance is expected to provide method handlers and subscription producers that satisfy
 * a given RPC protocol describing the signatures of remote methods and shape of data returned
 * by them.
 */ export { RpcChannel as default };

//# sourceMappingURL=channel.js.map